/**
 * Deterministic inquiry assistant ("automat poptávky").
 * Maps the visitor's intents and a few constraints to recommended solutions, products,
 * documents and a pre-filled inquiry e-mail. Rule based, no AI calls — same philosophy
 * as the screening in `src/utils/scoring/`.
 */
import { INQUIRY_EMAIL, PRODUCT_BY_ID, ProductId, offerPdfPage, BASICS_PDF_URL } from "../../data/products"

export type IntentId =
	| "integration"
	| "documents"
	| "web"
	| "reporting"
	| "assistant"
	| "analysis"
	| "realtime"
	| "operations"

export type DataPolicy = "onprem" | "cloud" | "unsure"
export type HumanApproval = "yes" | "no" | "unsure"

export interface IntentOption {
	id: IntentId
	title: string
	text: string
	/** Example systems / inputs shown as small tags */
	examples: string[]
}

export const INTENTS: IntentOption[] = [
	{
		id: "integration",
		title: "Integrace systémů",
		text: "Propojit CRM, e-shop, účetnictví nebo ERP tak, aby data tekla sama.",
		examples: ["CRM", "e-shop", "účetnictví", "ERP", "API"]
	},
	{
		id: "documents",
		title: "Zpracování dokumentů a kusovníků",
		text: "Vytěžit data z PDF výkresů, kusovníků (BOM), faktur, smluv nebo skenů.",
		examples: ["PDF", "OCR", "BOM", "faktury", "smlouvy"]
	},
	{
		id: "web",
		title: "Web, portál nebo aplikace na míru",
		text: "Rozhraní pro zaměstnance či zákazníky napojené na vaše data a AI.",
		examples: ["intranet", "klientský portál", "webová aplikace"]
	},
	{
		id: "reporting",
		title: "Automatizace reportingu a rutiny",
		text: "Pravidelné reporty, přepisování mezi systémy a generování dokumentů bez lidí.",
		examples: ["Excel", "Word", "Google Sheets", "e-mail"]
	},
	{
		id: "assistant",
		title: "AI asistent nad firemními daty",
		text: "Chatbot nebo agent, který odpovídá z vašich dokumentů, školení či helpdesku.",
		examples: ["helpdesk", "e-learning", "znalostní báze"]
	},
	{
		id: "realtime",
		title: "Transakce a události v reálném čase",
		text: "Platby, objednávky, logistika – rychle, deterministicky a bez AI.",
		examples: ["platby", "webhooky", "logistika"]
	},
	{
		id: "analysis",
		title: "Analýza procesů a návrh architektury",
		text: "Nevíte, kde začít? Zmapujeme procesy, dáme jim rating a navrhneme cílový stav.",
		examples: ["rating procesů", "audit", "architektura"]
	},
	{
		id: "operations",
		title: "Nasazení, provoz a dohled",
		text: "Dostat hotové řešení do produkce bez výpadků a držet nad ním SLA.",
		examples: ["deployment", "monitoring", "SLA"]
	}
]

export interface InquirySelection {
	intents: IntentId[]
	dataPolicy: DataPolicy
	humanApproval: HumanApproval
	note: string
}

export const EMPTY_SELECTION: InquirySelection = {
	intents: [],
	dataPolicy: "unsure",
	humanApproval: "unsure",
	note: ""
}

export interface RecommendationLink {
	label: string
	href: string
	external?: boolean
}

export interface Recommendation {
	intent: IntentId
	title: string
	text: string
	/** Recommended products in display priority */
	products: ProductId[]
	links: RecommendationLink[]
}

export interface AiAdvice {
	title: string
	text: string
}

export interface InquiryResult {
	recommendations: Recommendation[]
	/** Unique products across all recommendations, in catalogue order (Make SDK last) */
	products: ProductId[]
	ai: AiAdvice | null
	/** Extra note when human approval is required */
	humanApproval: string | null
}

/** Catalogue order used to sort merged product lists (keeps Make SDK last). */
const PRODUCT_ORDER: ProductId[] = ["gorbus", "neuron-machine", "light-gateway", "brokerhflow", "custom-app", "make-sdk"]

const OFFER_LINK = (page?: number, label = "Nabídka řešení (PDF)"): RecommendationLink => ({
	label,
	href: offerPdfPage(page),
	external: true
})

const ARTICLE_PLATFORM: RecommendationLink = {
	label: "Článek: Make, vlastní adaptér nebo přímé API",
	href: "/clanky/jak-vybrat-integracni-platformu.html"
}

const ARTICLE_AI_OR_AUTOMAT: RecommendationLink = {
	label: "Článek: AI nebo klasický automat?",
	href: "/clanky/ai-nebo-automat.html"
}

const ARTICLE_BASICS: RecommendationLink = {
	label: "Článek: Základy integrace B2B",
	href: "/clanky/zaklady-integrace-b2b.html"
}

const BASICS_LINK: RecommendationLink = { label: "Základy integrace B2B (PDF)", href: BASICS_PDF_URL, external: true }

/**
 * Builds one recommendation per selected intent. Pure function.
 */
const recommendFor = (intent: IntentId, selection: InquirySelection): Recommendation => {
	const onprem = selection.dataPolicy === "onprem"
	const hitl = selection.humanApproval === "yes"

	switch (intent) {
		case "integration":
			return {
				intent,
				title: "Integrace systémů a adaptéry",
				text: onprem
					? "Propojíme systémy adaptéry na míru přímo ve vaší síti: sběrnice GoRBus směruje události mezi CRM, e-shopem a účetnictvím, brána Light B2B Gateway tvoří jednotný vstupní bod pro API. Data neopouštějí vaši infrastrukturu."
					: "Propojíme systémy adaptéry na míru (API, event-driven). Sběrnice GoRBus směruje události mezi CRM, e-shopem a účetnictvím, brána Light B2B Gateway tvoří jednotný vstupní bod. Pokud už používáte Make.com, doplníme Make SDK pro bezpečný přístup k lokálním datům.",
				products: onprem ? ["gorbus", "light-gateway"] : ["gorbus", "light-gateway", "make-sdk"],
				links: [OFFER_LINK(4, "Synergie ekosystému (PDF)"), ARTICLE_PLATFORM, BASICS_LINK]
			}
		case "documents":
			return {
				intent,
				title: "Vytěžení dokumentů a kusovníků",
				text: hitl
					? "Neuron Machine zpracuje PDF, OCR a extrakci dat na pozadí v izolovaných procesech. Výsledek zkontroluje pracovník v portálu BrokerHFlow (PDF vedle vytěžených dat) a teprve schválená data jdou do ERP. Typický efekt: výkres za 5 sekund místo 20 minut."
					: "Neuron Machine zpracuje PDF, OCR a extrakci dat na pozadí v izolovaných procesech a výstup předá jako Excel, Word nebo JSON rovnou do ERP. Typický efekt: výkres za 5 sekund místo 20 minut, bez ručního přepisu.",
				products: hitl ? ["neuron-machine", "brokerhflow", "light-gateway"] : ["neuron-machine", "light-gateway"],
				links: [OFFER_LINK(16, "Případová studie: kusovníky (PDF)"), ARTICLE_AI_OR_AUTOMAT]
			}
		case "web":
			return {
				intent,
				title: "Aplikace a portál na míru",
				text: "Navrhneme webové či intranetové rozhraní přesně podle vašich procesů: nahrání souboru, náhled, spuštění analýzy jedním kliknutím. Backend zajistí Light B2B Gateway (API) a podle potřeby portál BrokerHFlow pro kontrolu dokumentů.",
				products: ["custom-app", "light-gateway", "brokerhflow"],
				links: [OFFER_LINK(12, "Aplikace na zakázku (PDF)")]
			}
		case "reporting":
			return {
				intent,
				title: "Automatizace reportingu a rutinní administrativy",
				text: onprem
					? "Rutiny na sběrnici GoRBus nebo exportní pluginy Neuron Machine vygenerují Excel či Word reporty přímo z vašich databází a složek – vše ve vaší síti, bez cloudu."
					: "Reporty vznikají automaticky: GoRBus a Neuron Machine je sestaví z databází a dokumentů do Excelu nebo Wordu. Pro firmy na Make.com nabízíme Make SDK, které bezpečně pošle nekritická data do cloudového scénáře (Google Sheets, e-mailový souhrn).",
				products: onprem ? ["gorbus", "neuron-machine"] : ["gorbus", "neuron-machine", "make-sdk"],
				links: [OFFER_LINK(15, "Případová studie: reporting (PDF)"), ARTICLE_AI_OR_AUTOMAT]
			}
		case "assistant":
			return {
				intent,
				title: "AI asistent nad vašimi daty",
				text: "Light B2B Gateway přijme dotaz z webu či helpdesku a nasměruje jej na AI agenta (Dify, Claude, případně lokální model) s kontextem vašich dokumentů. Výsledek: okamžitá odpověď 24/7 a méně zátěže pro lektory a podporu.",
				products: ["light-gateway", "custom-app"],
				links: [OFFER_LINK(18, "Případová studie: AI lektor (PDF)"), ARTICLE_AI_OR_AUTOMAT]
			}
		case "realtime":
			return {
				intent,
				title: "Deterministické transakce v reálném čase",
				text: "Sběrnice GoRBus v jazyce Go odbaví platby, objednávky či logistické události pod 10 ms s dostupností 99,999 %. AI je zde záměrně vyloučena – kritické operace řídí exaktní, auditovatelný kód.",
				products: ["gorbus"],
				links: [OFFER_LINK(17, "Případová studie: finanční transakce (PDF)"), ARTICLE_BASICS]
			}
		case "analysis":
			return {
				intent,
				title: "Analýza a rating procesů",
				text: "Začneme úvodní konzultací zdarma a hloubkovou analýzou (1–3 týdny). Každý proces dostane rating podle efektivity, nákladů a složitosti; výstupem je seznam priorit, odhad přínosu a návrh cílové architektury – včetně toho, kde zachovat determinismus a kde použít AI.",
				products: [],
				links: [OFFER_LINK(11, "Strategie nasazení (PDF)"), { label: "Ceník a průběh spolupráce", href: "#pricing" }, BASICS_LINK]
			}
		case "operations":
		default:
			return {
				intent: "operations",
				title: "Nasazení do produkce a odborný dohled",
				text: "Nasazení bez výpadků, zabezpečení cílového prostředí, monitoring a logování. Po spuštění držíme dohled podle SLA: reakce na kritický incident do 4 hodin, záruka 6 měsíců. Předáváme dokumentaci i zdrojové kódy.",
				products: [],
				links: [{ label: "Záruka a SLA", href: "/zaruka.html" }, OFFER_LINK(20, "Proces implementace (PDF)")]
			}
	}
}

/** Intents where an AI model choice is relevant. */
const AI_INTENTS: IntentId[] = ["documents", "assistant", "reporting", "web"]

const aiAdviceFor = (selection: InquirySelection): AiAdvice | null => {
	if (!selection.intents.some(i => AI_INTENTS.includes(i))) return null

	switch (selection.dataPolicy) {
		case "onprem":
			return {
				title: "AI modely: lokálně, data neopustí vaši síť",
				text: "Pro citlivé dokumenty nasadíme lokální modely (LLaMA 3.1 přes Ollama) v izolovaném jádru Neuron Machine. Cloudové modely (Claude, Gemini, Groq) použijeme jen pro nekritická data, pokud to schválíte."
			}
		case "cloud":
			return {
				title: "AI modely: cloud podle typu úlohy",
				text: "Claude (Anthropic) pro přesnou extrakci z dlouhých dokumentů a smluv a pro agenty s velkým kontextem, Gemini pro multimodální úlohy (výkresy, obrázky), Groq API pro rychlé a levné dávkové zpracování. Klíče a data chráníme v bráně Light B2B Gateway."
			}
		default:
			return {
				title: "AI modely: hybridní přístup",
				text: "Citlivé dokumenty zpracujeme lokálně (LLaMA přes Ollama), nekritická data rychle v cloudu (Claude, Gemini nebo Groq). Které konkrétní modely použít, rozhodneme v analýze podle objemu, přesnosti a ochrany dat."
			}
	}
}

/**
 * Main entry: builds recommendations for the selection. Pure and deterministic.
 */
export const buildInquiryResult = (selection: InquirySelection): InquiryResult => {
	const recommendations = selection.intents.map(intent => recommendFor(intent, selection))
	const productSet = new Set<ProductId>()
	recommendations.forEach(r => r.products.forEach(p => productSet.add(p)))

	const hitl = selection.humanApproval === "yes"
	if (hitl && recommendations.length > 0 && !productSet.has("brokerhflow")) {
		productSet.add("brokerhflow")
	}

	return {
		recommendations,
		products: PRODUCT_ORDER.filter(id => productSet.has(id)),
		ai: aiAdviceFor(selection),
		humanApproval: hitl
			? "Výstupy, u kterých musí mít poslední slovo člověk, povedeme přes portál BrokerHFlow (kontrola a schválení před zápisem do ERP). U scénářů na Make.com lze využít i modul lidského schválení přímo ve scénáři."
			: null
	}
}

const DATA_POLICY_LABEL: Record<DataPolicy, string> = {
	onprem: "data musí zůstat ve firmě (on-premise)",
	cloud: "cloudové zpracování je v pořádku",
	unsure: "zatím nevíme"
}

const HUMAN_APPROVAL_LABEL: Record<HumanApproval, string> = {
	yes: "ano, výstupy musí schválit člověk",
	no: "ne, plně automatický provoz",
	unsure: "zatím nevíme"
}

export const intentTitle = (id: IntentId) => INTENTS.find(i => i.id === id)?.title || id

/**
 * Plain-text summary of the inquiry (shown to the user and used as the e-mail body).
 */
export const buildInquirySummary = (selection: InquirySelection, result: InquiryResult): string => {
	const lines: string[] = []
	lines.push("Dobrý den,")
	lines.push("")
	lines.push("mám zájem o nezávaznou konzultaci k následujícímu záměru:")
	lines.push("")
	lines.push("Co potřebujeme vyřešit:")
	selection.intents.forEach(i => lines.push(`- ${intentTitle(i)}`))
	lines.push("")
	lines.push(`Umístění dat: ${DATA_POLICY_LABEL[selection.dataPolicy]}`)
	lines.push(`Lidské schválení výstupů: ${HUMAN_APPROVAL_LABEL[selection.humanApproval]}`)
	if (result.products.length > 0) {
		lines.push(`Produkty doporučené automatem: ${result.products.map(id => PRODUCT_BY_ID[id].name).join(", ")}`)
	}
	if (selection.note.trim()) {
		lines.push("")
		lines.push("Poznámka:")
		lines.push(selection.note.trim())
	}
	lines.push("")
	lines.push("Prosím o návrh dalšího postupu.")
	lines.push("")
	lines.push("Děkuji.")
	return lines.join("\n")
}

export const buildInquirySubject = (selection: InquirySelection): string => {
	const titles = selection.intents.map(intentTitle)
	const head = titles.slice(0, 2).join(", ")
	const rest = titles.length > 2 ? ` (+${titles.length - 2})` : ""
	return `Poptávka z webu: ${head}${rest}`
}

/**
 * mailto: link with the pre-filled subject and body. Kept well under common URL length limits.
 */
export const buildInquiryMailto = (selection: InquirySelection, result: InquiryResult): string => {
	const subject = encodeURIComponent(buildInquirySubject(selection))
	const body = encodeURIComponent(buildInquirySummary(selection, result))
	return `mailto:${INQUIRY_EMAIL}?subject=${subject}&body=${body}`
}
